import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import {
  AccountManager,
  SettingsManager,
  SiteConfigManager,
  useApplicationState,
  useSettings,
} from '@atlas/core';

import { capitalize } from 'src/common/utils/capitalize';

export const OddsFormatContext = createContext();

export const OddsFormatProvider = ({ children }) => {
  const {
    authenticated,
    preferences: { data: preferences },
  } = useApplicationState();

  const { oddsFormat: rawOddsFormatFromSettings } = useSettings();

  const rawDefaultOdds = SiteConfigManager.getConfig('config.config.sportsbook.defaultOddsType');
  const defaultOddsType = rawDefaultOdds ? capitalize(rawDefaultOdds) : null;

  const serverOddsFormat = preferences?.oddsFormat && capitalize(preferences.oddsFormat);

  const oddsFormatFromSettings = rawOddsFormatFromSettings
    ? capitalize(rawOddsFormatFromSettings)
    : null;

  const [oddsFormat, setOddsFormat] = useState(() => {
    if (authenticated && serverOddsFormat) {
      return serverOddsFormat;
    }
    return oddsFormatFromSettings || defaultOddsType;
  });

  useEffect(() => {
    if (authenticated && serverOddsFormat) {
      setOddsFormat(serverOddsFormat);
    }
  }, [authenticated, serverOddsFormat]);

  useEffect(() => {
    if (oddsFormat) {
      if (rawOddsFormatFromSettings !== oddsFormat) {
        SettingsManager.updateSettings({
          oddsFormat: oddsFormat,
        });
      }
    } else {
      setOddsFormat(defaultOddsType);
    }
  }, [authenticated, oddsFormat, defaultOddsType, rawOddsFormatFromSettings]);

  const changeOddsFormat = useCallback(
    oddsType => {
      const newOddsFormat = capitalize(oddsType);

      setOddsFormat(newOddsFormat);

      if (authenticated) {
        AccountManager.setClientPreferences({
          ...preferences,
          oddsFormat: newOddsFormat,
        });
      }
    },
    [authenticated, preferences],
  );

  const state = useMemo(() => ({ changeOddsFormat, oddsFormat }), [oddsFormat, changeOddsFormat]);

  return <OddsFormatContext.Provider value={state}>{children}</OddsFormatContext.Provider>;
};

OddsFormatProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useOddsFormat = () => useContext(OddsFormatContext);
